import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient, { isApiError } from '../api/client';
import '../styles/email-verification.css';

type Status = 'loading' | 'ready' | 'confirmed' | 'expired' | 'completed' | 'invalid';

const WarnIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="#e05252" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="#38A16A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

interface ResolveResponse {
    email: string;
    status: string;
    expires_at: string;
}

const AccountDeletionConfirmPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [status, setStatus] = useState<Status>('loading');
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) {
            setStatus('invalid');
            return;
        }
        apiClient.get<ResolveResponse>(`/api/account-deletion/${token}`)
            .then(({ data }) => {
                setEmail(data.email);
                setStatus(data.status === 'confirmed' ? 'confirmed' : 'ready');
            })
            .catch((err: unknown) => {
                if (isApiError(err) && err.response.status === 410) {
                    const data = err.response.data as { error?: string } | undefined;
                    setStatus(data?.error?.includes('already been deleted') ? 'completed' : 'expired');
                } else {
                    setStatus('invalid');
                }
            });
    }, [token]);

    const handleConfirm = async () => {
        if (!token) return;
        setSubmitting(true);
        try {
            await apiClient.post(`/api/account-deletion/${token}/confirm`);
            setStatus('confirmed');
        } catch {
            setStatus('invalid');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="ev-page">
            <div className="ev-card">
                {status === 'loading' && (
                    <>
                        <div className="ev-icon-wrap ev-icon-wrap--loading">
                            <div className="ev-spinner" />
                        </div>
                        <h1 className="ev-title">Checking your link…</h1>
                        <p className="ev-body">One moment while we verify this request.</p>
                    </>
                )}

                {status === 'ready' && (
                    <>
                        <div className="ev-icon-wrap ev-icon-wrap--error"><WarnIcon /></div>
                        <h1 className="ev-title">Delete your account?</h1>
                        <p className="ev-body">
                            An admin received your request to delete the WeaveWords account for <strong>{email}</strong>.
                            This will permanently erase your vocabulary, collections, spaced-repetition progress,
                            statistics, and settings. This cannot be undone.
                        </p>
                        <div className="ev-actions">
                            <button className="ev-btn" style={{ background: 'linear-gradient(135deg, #f47174 0%, #e05252 100%)', boxShadow: '0 4px 16px rgba(224, 82, 82, 0.35)' }} onClick={handleConfirm} disabled={submitting}>
                                {submitting ? 'Submitting…' : 'Yes, I understand that my info will be entirely deleted.'}
                            </button>
                            <button className="ev-btn ev-btn--secondary" onClick={() => navigate('/')}>Cancel</button>
                        </div>
                    </>
                )}

                {status === 'confirmed' && (
                    <>
                        <div className="ev-icon-wrap ev-icon-wrap--success"><CheckIcon /></div>
                        <h1 className="ev-title">Confirmed</h1>
                        <p className="ev-body">
                            Thanks — we've recorded your confirmation. An admin will complete the deletion shortly.
                        </p>
                        <div className="ev-actions">
                            <button className="ev-btn ev-btn--secondary" onClick={() => navigate('/')}>Back to Weavewords</button>
                        </div>
                    </>
                )}

                {status === 'completed' && (
                    <>
                        <div className="ev-icon-wrap ev-icon-wrap--success"><CheckIcon /></div>
                        <h1 className="ev-title">Account already deleted</h1>
                        <p className="ev-body">This account has already been deleted. There's nothing more to do here.</p>
                        <div className="ev-actions">
                            <button className="ev-btn ev-btn--secondary" onClick={() => navigate('/')}>Back to Weavewords</button>
                        </div>
                    </>
                )}

                {status === 'expired' && (
                    <>
                        <div className="ev-icon-wrap ev-icon-wrap--error"><WarnIcon /></div>
                        <h1 className="ev-title">This link has expired</h1>
                        <p className="ev-body">
                            This confirmation link is no longer valid. If you still want your account deleted,
                            please email <a href="mailto:admin@weavewords.org">admin@weavewords.org</a> again.
                        </p>
                        <div className="ev-actions">
                            <button className="ev-btn ev-btn--secondary" onClick={() => navigate('/')}>Back to Weavewords</button>
                        </div>
                    </>
                )}

                {status === 'invalid' && (
                    <>
                        <div className="ev-icon-wrap ev-icon-wrap--error"><WarnIcon /></div>
                        <h1 className="ev-title">Link not found</h1>
                        <p className="ev-body">
                            We couldn't find this deletion request. If you believe this is a mistake, email{' '}
                            <a href="mailto:admin@weavewords.org">admin@weavewords.org</a>.
                        </p>
                        <div className="ev-actions">
                            <button className="ev-btn ev-btn--secondary" onClick={() => navigate('/')}>Back to Weavewords</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AccountDeletionConfirmPage;
